import { getAbstractAccountAddress } from '../utils/getAbstractAccountAddress';
import { setupSmartAccount } from '../lib/permissionless/accounts/setupSmartAccount';
import { needsUpgrade } from '../utils/needsUpgrade';
import { sendUserOp } from './sendUserOp';
import { userOpsStore } from '../store/userOpsStore';
/**
 * Manages smart account client lifecycle - creation, caching, and reset
 */
export class SmartAccountSession {
    smartAccount;
    config;
    constructor(config) {
        if (!config.newAccountImplementationType) {
            throw new Error('newAccountImplementationType is required');
        }
        if (!config.paymasterProxyUrl) {
            throw new Error('paymasterProxyUrl is required');
        }
        if (!config.paymasterProxyAuthSecret) {
            throw new Error('paymasterProxyAuthSecret is required');
        }
        this.config = config;
    }
    /**
     * Get or create smart account (cached)
     */
    async getSmartAccount(args) {
        const { signer } = args;
        if (!this.smartAccount) {
            const { paymasterProxyUrl, paymasterProxyAuthSecret } = this.config;
            if (!paymasterProxyUrl) {
                throw new Error('paymasterProxyUrl is required');
            }
            if (!paymasterProxyAuthSecret) {
                throw new Error('paymasterProxyAuthSecret is required');
            }
            this.smartAccount = setupSmartAccount({
                ...this.config,
                signer,
                rpcUrl: this.config.aaRpcUrl,
                fetchAccessTokenFn: this.config.fetchAccessTokenFn,
                paymasterProxyUrl,
                paymasterProxyAuthSecret,
            }).then((smartAccount) => ({
                ...smartAccount,
                sendUserOp: async (args) => sendUserOp({
                    ...args,
                    smartAccountSession: this,
                    newAccountImplementationType: this.config.newAccountImplementationType,
                    enableUsdc: this.config.enableUsdc ?? false,
                }),
            }));
        }
        return this.smartAccount;
    }
    /**
     * Fetch counterfactual address before account deployment
     */
    async getAbstractAccountAddress(args) {
        const { rootKeyAddress } = args;
        const { paymasterProxyUrl, paymasterProxyAuthSecret } = this.config;
        if (!paymasterProxyUrl) {
            throw new Error('paymasterProxyUrl is required');
        }
        if (!paymasterProxyAuthSecret) {
            throw new Error('paymasterProxyAuthSecret is required');
        }
        return getAbstractAccountAddress({
            paymasterProxyUrl,
            paymasterProxyAuthSecret,
            rootKeyAddress,
            newAccountImplementationType: this.config.newAccountImplementationType,
        });
    }
    /**
     * Clear cached client (e.g., after upgrade)
     */
    async reset() {
        try {
            const address = (await this.smartAccount)?.address;
            if (address) {
                userOpsStore.getState().reset(address);
            }
        }
        finally {
            this.smartAccount = undefined;
        }
    }
    /**
     * Get cached client address if available
     */
    async getCachedAddress() {
        return (await this.smartAccount)?.address;
    }
    /**
     * Check if account needs upgrade (simple -> modular)
     */
    needsUpgrade(client) {
        return needsUpgrade(this.config.newAccountImplementationType, client.type);
    }
    /**
     * Get config for accessing smart account properties
     */
    getConfig() {
        return this.config;
    }
}
